import useConfigValues from '../../ReduxFunctions/useConfigValues';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllData, fetchData, fetchUpdateData } from '../../Helpers/externapi';
import { formatDate, calculateAge } from '../../CommonFunctions/CommonFunctions';
import { setHospitalImage } from '../../ReduxFunctions/ReduxSlice';
import { logToCloudWatch } from '../../Helpers/cloudwatchLogger';

const createInitialFormData = () => ({
    FullName: '',
    MobileNumber: '',
    Cardnumber: '',
    Gender: '',
    DateofBirth: '',
    Age: '',
    Address: '',
    Appointment: '',
    DateAndTime: '',
    DoctorName: '',
    ServiceType: null,
    MemberDependentId: null,
    LabPercentage: null,
    PharmacyPercentage: null,
    PaidAmount: '',
    TotalAmount: '',
    HospitalPoliciesId: null,
});

const formatDatabaseDateTime = (value) => {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const getServiceLabel = (policyId) => {
    if (policyId === 1) {
        return 'Free Consultation';
    }

    if (policyId === 2) {
        return 'Lab Investigation';
    }

    return 'Pharmacy Discount';
};

const useConfirmBookingController = () => {
    const configValues = useConfigValues('hospitalImagesURL', 'LogoWithoutName');
    const hospitalImage = useSelector((state) => state.hospitalImage);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { Id: consultationHash } = useParams();

    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [consultationData, setConsultationData] = useState([]);
    const [formData, setFormData] = useState(createInitialFormData);
    const [formErrors, setFormErrors] = useState({ DateAndTime: '', ServiceType: '' });
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [hospitalName, setHospitalName] = useState(sessionStorage.getItem('hospitalName'));
    const [hospitalLogo, setHospitalLogo] = useState(sessionStorage.getItem('hospitalImage'));
    const [serviceTypes, setServiceTypes] = useState([]);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [ohoLogo, setOhoLogo] = useState();
    const [isSuccess, setIsSuccess] = useState(false);

    const consultation = consultationData[0];
    const logGroupName = process.env.REACT_APP_LOGGER;
    const logStreamName = consultation
        ? `${consultation.HospitalId} (${consultation.HospitalName})-${new Date().toISOString().split('T')[0]}`
        : '';

    const navigateToBooking = () => {
        navigate('/bookconsultation', { replace: true });
        window.history.replaceState(null, '', '/bookconsultation');
    };

    const persistSession = () => {
        const currentTime = Date.now();
        const hospitalExpirationTime = currentTime + 24 * 60 * 60 * 1000;
        const memberExpirationTime = currentTime + 10 * 60 * 1000;

        sessionStorage.setItem('hospitalName', consultation.HospitalName);
        sessionStorage.setItem('hospitalImage', hospitalImage);
        sessionStorage.setItem('hospitalId', consultation.HospitalId);
        sessionStorage.setItem('hospitalTime', hospitalExpirationTime);
        sessionStorage.setItem('memberId', consultation.MemberId);
        sessionStorage.setItem('memberTime', memberExpirationTime);
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchBooking = async () => {
            try {
                const data = await fetchAllData(`lambdaAPI/BookingConsultation/GetByConsultationHashCode/${consultationHash}`);
                const booking = data?.[0];

                setConsultationData(data || []);

                if (!booking) {
                    return;
                }

                setFormData({
                    FullName: booking.Name,
                    MobileNumber: booking.MobileNumber,
                    Cardnumber: booking.CardNumber,
                    Gender: booking.Gender,
                    DateofBirth: formatDate(booking.DateofBirth),
                    Age: calculateAge(booking.DateofBirth),
                    Address: booking.Address,
                    Appointment: booking.Appointment,
                    DateAndTime: booking.AppointmentDate,
                    DoctorName: booking.DoctorName,
                    ServiceType: booking.ServiceTypeId,
                    MemberDependentId: booking.MemberDependentId,
                    LabPercentage: booking.LabInvestigationPercentage,
                    PharmacyPercentage: booking.PharmacyDiscountPercentage,
                    PaidAmount: booking.PaidAmount,
                    TotalAmount: booking.TotalAmount,
                    HospitalPoliciesId: booking.HospitalPoliciesId,
                });
                setHospitalLogo(booking.Image);
                setHospitalName(booking.HospitalName);
            } catch (error) {
                console.error(`Error fetching BookingConsultation/GetById/${consultationHash}:`, error);
            } finally {
                setIsDataFetched(true);
            }
        };

        const fetchServiceTypes = async () => {
            const services = await fetchData('HospitalServices/all', { skip: 0, take: 0 });
            setServiceTypes(services || []);
        };

        fetchBooking();
        fetchServiceTypes();
    }, [consultationHash]);

    useEffect(() => {
        if (!configValues.length) {
            return;
        }

        const imageUrl = configValues.find((value) => value.ConfigKey === 'hospitalImagesURL');
        if (imageUrl && hospitalLogo) {
            dispatch(setHospitalImage(imageUrl.ConfigValue + hospitalLogo));
        }

        setOhoLogo(configValues.find((value) => value.ConfigKey === 'LogoWithoutName'));
    }, [configValues, dispatch, hospitalLogo]);

    const onChangeDateTime = (dates) => {
        const date = formatDatabaseDateTime(dates.toString().slice(0, 24));
        setFormErrors((errors) => ({ ...errors, DateAndTime: '' }));
        setFormData((data) => ({ ...data, DateAndTime: date }));
    };

    const onChangeHandler = (event) => {
        const { name, value } = event.target;

        if (name === 'ServiceType') {
            setFormData((data) => ({ ...data, [name]: parseInt(value, 10) }));
            if (value) {
                setFormErrors((errors) => ({ ...errors, ServiceType: '' }));
            }
            return;
        }

        if (name === 'LabPercentage' || name === 'PharmacyPercentage') {
            if (/^\d*$/.test(value) && value.length <= 3) {
                setFormData((data) => ({ ...data, [name]: value === '' ? '' : parseInt(value, 10) }));
            }
            return;
        }

        setFormData((data) => ({ ...data, [name]: value }));
    };

    const validateForm = () => {
        const requiresAppointmentDate = [1, 2].includes(formData.HospitalPoliciesId);
        if (requiresAppointmentDate && !formData.DateAndTime) {
            setFormErrors((errors) => ({ ...errors, DateAndTime: 'Please select appointment date & time *' }));
            return false;
        }

        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!consultation || !validateForm()) {
            return;
        }

        try {
            setSubmitLoading(true);
            const statuses = await fetchAllData('lambdaAPI/Status/all');
            const visitedStatus = statuses.find((status) => status.Value === 'Visited');
            const updateData = [
                { TableName: 'BookingConsultation', ColumnName: 'PharmacyDiscountPercentage', ColumnValue: formData.PharmacyPercentage, TableId: consultation.BookingConsultationId },
                { TableName: 'BookingConsultation', ColumnName: 'LabInvestigationPercentage', ColumnValue: formData.LabPercentage, TableId: consultation.BookingConsultationId },
                { TableName: 'BookingConsultation', ColumnName: 'Status', ColumnValue: visitedStatus.StatusId, TableId: consultation.BookingConsultationId },
                { TableName: 'BookingConsultation', ColumnName: 'PaidAmount', ColumnValue: formData.PaidAmount, TableId: consultation.BookingConsultationId },
                { TableName: 'BookingConsultation', ColumnName: 'TotalAmount', ColumnValue: formData.TotalAmount, TableId: consultation.BookingConsultationId },
            ];
            const updateResponse = await fetchUpdateData('lambdaAPI/BookingConsultation/Update', updateData);

            if (!updateResponse) {
                return;
            }

            await logToCloudWatch(logGroupName, logStreamName, {
                event: `${getServiceLabel(formData.HospitalPoliciesId)} Approved Successfully`,
                details: { response: updateResponse },
            });

            persistSession();
            setIsSuccess(true);
            setFormErrors({ DateAndTime: '', ServiceType: '' });

            setTimeout(() => {
                setFormData((data) => ({
                    ...data,
                    DateAndTime: '',
                    DoctorName: '',
                    ServiceType: null,
                    LabPercentage: null,
                    PharmacyPercentage: null,
                    PaidAmount: '',
                    TotalAmount: '',
                }));
                navigateToBooking();
            }, 3000);
        } finally {
            setSubmitLoading(false);
        }
    };

    return {
        formData,
        formErrors,
        hospitalImage,
        hospitalLogo,
        hospitalName,
        isDataFetched,
        isSuccess,
        ohoLogo,
        onChangeDateTime,
        onChangeHandler,
        handleSubmit,
        serviceTypes,
        submitLoading,
        windowSize,
    };
};

export default useConfirmBookingController;
